import Button from '@/components/Buttons/Button';
import { useAnalytics } from '@/services/analytics';
import { useGenerateTranslationGroup, useGetTranslationGroup } from '@/state/translations';
import { AppRoutes } from '@/utils/types';
import styled from '@emotion/styled';
import { Popover, PopoverProps, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentDraftGroupId } from '../../hooks/useCurrentDraftGroupId';
import { useSubmitTranslationDrafts } from '../../hooks/useSubmitTranslationDrafts';
import TitleInput from '../TitleInput';
import TranslationList from './TranslationList';

const paperSx = {
    borderRadius: '20px',
    boxShadow: '0px 25px 100px 0px #1C1D2133',
    '&:focus': {
        outline: 'none',
    },
};

const DropDownContent = styled.div`
    width: 33rem;
    padding: 1.6rem 2.4rem;
    position: relative;
`;

const Title = styled(Typography)`
    user-select: none;
`;

const TitleContainer = styled.div`
    width: 100%;
    border-bottom: 1px solid ${({ theme }) => theme.colors.black[50]};
    padding-bottom: 1.6rem;
    margin-bottom: 1.6rem;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
`;

const TranslationName = styled(TitleInput)`
    width: 100%;
    line-height: 2rem;
`;

const LanguagesTitle = styled(Title)`
    margin-bottom: 1.2rem;
`;

const ButtonContainer = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 1.6rem 2.4rem;
    border-top: 1px solid ${({ theme }) => theme.colors.black[300]};
    background-color: #fff;
`;

const GenerateButton = styled(Button)`
    height: 4.4rem;
`;

interface IProps extends PopoverProps {}

function TranslationGenerateDropdown(props: IProps) {
    const groupId = useCurrentDraftGroupId();
    const { data: group } = useGetTranslationGroup(groupId!);
    const { submitTranslationDrafts } = useSubmitTranslationDrafts();
    const { mutateAsync: generateTranslationGroup } = useGenerateTranslationGroup();
    const [isLoading, setLoading] = useState(false);
    const navigate = useNavigate();
    const translations = group?.translations || [];
    const mixpanel = useAnalytics();
    async function handleGenerateClick() {
        setLoading(true);
        try {
            sendGenerateTrackingEvent('click');
            await submitTranslationDrafts();
            await generateTranslationGroup({ id: groupId! });
            sendGenerateTrackingEvent('success');
        } catch (error: any) {
            sendGenerateTrackingEvent('error', {
                error: {
                    kind: error?.response?.data?.kind,
                    description: error?.response?.data?.description,
                },
            });
        } finally {
            setLoading(false);
        }
        navigate(AppRoutes.Translation);
    }

    const sendGenerateTrackingEvent = (eventType: 'click' | 'success' | 'error', extraData = {}) => {
        if (!group) return;

        const trackEventName = 'translate-video';
        const languagesArr = group?.translations?.map(translation => translation.language) || [];
        const isProofreading = group?.translations?.some(translation => translation.proofreading);

        const mixpanelProps = {
            languages: languagesArr.join(','),
            num_languages: languagesArr.length,
            duration: group.duration || 0,
            proofread: isProofreading,
            source_language: group.source_language || null,
        };

        mixpanel?.track(trackEventName, {
            event: eventType,
            ...mixpanelProps,
            ...extraData,
        });
    };

    return (
        <Popover
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: -12,
                horizontal: 'right',
            }}
            slotProps={{ paper: { sx: paperSx } }}
            {...props}>
            <DropDownContent>
                <TitleContainer>
                    <Title variant="h5">Video name</Title>
                    <TranslationName fontSize={16} staticWidth />
                </TitleContainer>
                <LanguagesTitle variant="h5">Languages&nbsp;{`(${translations.length})`}</LanguagesTitle>
                <TranslationList translations={translations} />
                <ButtonContainer>
                    <GenerateButton
                        data-testid="translation-generate-dropdown-generate-button"
                        variant="contained"
                        color="primary"
                        fullWidth
                        isLoading={isLoading}
                        onClick={handleGenerateClick}>
                        Translate&nbsp;{translations.length}&nbsp;videos
                    </GenerateButton>
                </ButtonContainer>
            </DropDownContent>
        </Popover>
    );
}

export default TranslationGenerateDropdown;
